import { useEffect, useState } from "react";
import { useShowMessage } from "../Common/showMessage";

export default function useBluetoothDevice(connectDevice, setConnectDevice) {
  const showMessage = useShowMessage();

  // Check if the device/browser supports Bluetooth
  const checkBluetoothSupport = async () => {
    try {
      const available = await navigator.bluetooth.getAvailability();
      if (!available) {
        showMessage({
          type: "error",
          message: "Web Bluetooth API is not supported on this device/browser.",
        });
      }
      return available;
    } catch (error) {
      console.error("Bluetooth Support Error:", error);
      showMessage({
        type: "error",
        message: "An error occurred while checking Bluetooth support.",
      });
      return false;
    }
  };

  // Request a Bluetooth device connection
  const connectToBluetooth = async (deviceName = null, serviceUUID = null) => {
    try {
      // If a device is already connected, try to reconnect to it
      if (connectDevice) {
        if (connectDevice.gatt.connected) {
          console.log("Reusing previously connected device.");
          return connectDevice;
        } else {
          console.log("Connected Device: ", connectDevice);
          console.log(
            "Previously connected device is no longer connected. Disconnecting..."
          );
          setConnectDevice(null);
          await connectDevice.gatt.connect();
          return connectDevice;
        }
      }

      // Otherwise, request a new device
      const options = deviceName
        ? { filters: [{ name: deviceName }], optionalServices: [serviceUUID] }
        : { acceptAllDevices: true, optionalServices: [serviceUUID] };
      const device = await navigator.bluetooth.requestDevice(options);
      console.log("Device: ", device);
      setConnectDevice(device);
      return device;
    } catch (error) {
      showMessage({
        type: "error",
        message:
          error.name === "NotFoundError"
            ? "Bluetooth device selection was canceled by the user."
            : "An error occurred while selecting the Bluetooth device.",
      });
      return null;
    }
  };

  // Discover services and return the first writable characteristic
  const discoverDeviceServices = async (device, serviceUUID) => {
    try {
      if (!device.gatt) {
        throw new Error("GATT server is not available on this device.");
      }
      const server = await device.gatt.connect();
      const service = await server.getPrimaryService(serviceUUID);
      const characteristics = await service.getCharacteristics();
      const writableCharacteristic = characteristics.find(
        (char) =>
          char.properties.write && !char.properties.authenticatedSignedWrites
      );
      if (!writableCharacteristic) {
        showMessage({
          type: "error",
          message: "No writable characteristic found.",
        });
      }
      return writableCharacteristic;
    } catch (error) {
      showMessage({
        type: "error",
        message:
          error.message ||
          "An error occurred while discovering device services.",
      });
      return null;
    }
  };

  return {
    checkBluetoothSupport,
    connectToBluetooth,
    discoverDeviceServices,
  };
}
